import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Warehouse, Plus, Package, MapPin, Grid3x3, Layers, Activity } from 'lucide-react';
import {
  storageLocationsApi,
  YardStorageLocation,
  StorageLocationType,
  StorageLocationStatus,
} from '@/api/services/yard.api';
import { locationsApi, Location } from '@/api/services/locations.api';
import { toast } from 'sonner';
import {
  PageHeader,
  StatusBadge,
  MetricCard,
  KPIGrid,
  DataTable,
  Column,
  TableFilters,
} from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function YardStorageLocations() {
  const navigate = useNavigate();
  const [storageLocations, setStorageLocations] = useState<YardStorageLocation[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<YardStorageLocation | null>(null);
  const [stats, setStats] = useState({
    totalLocations: 0,
    racks: 0,
    bins: 0,
    utilization: 0,
  });

  const [formData, setFormData] = useState({
    locationId: '',
    code: '',
    name: '',
    type: 'RACK' as StorageLocationType,
    zone: '',
    aisle: '',
    rack: '',
    level: '',
    bin: '',
    maxWeight: '',
    maxVolume: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await storageLocationsApi.getAll({
        search: search || undefined,
        status: statusFilter ? (statusFilter as StorageLocationStatus) : undefined,
        type: typeFilter ? (typeFilter as StorageLocationType) : undefined,
      });
      setStorageLocations(response.data);

      // Calculate stats
      const total = response.data.length;
      const racks = response.data.filter((loc: YardStorageLocation) => loc.type === 'RACK').length;
      const bins = response.data.filter((loc: YardStorageLocation) => loc.type === 'BIN').length;
      const occupied = response.data.filter(
        (loc: YardStorageLocation) => loc.status === 'OCCUPIED'
      ).length;
      const utilization = total > 0 ? Math.round((occupied / total) * 100) : 0;

      setStats({ totalLocations: total, racks, bins, utilization });
    } catch (error) {
      console.error('Error fetching storage locations:', error);
      toast.error('Failed to load storage locations');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter]);
  const fetchLocations = useCallback(async () => {
    try {
      const response = await locationsApi.getAll({});
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchLocations();
  }, [fetchData, fetchLocations]);

  const handleCreate = async () => {
    try {
      await storageLocationsApi.create({
        locationId: formData.locationId,
        code: formData.code,
        name: formData.name,
        type: formData.type,
        zone: formData.zone || undefined,
        aisle: formData.aisle || undefined,
        rack: formData.rack || undefined,
        level: formData.level || undefined,
        bin: formData.bin || undefined,
        maxWeight: formData.maxWeight ? parseFloat(formData.maxWeight) : undefined,
        maxVolume: formData.maxVolume ? parseFloat(formData.maxVolume) : undefined,
      });
      toast.success('Storage location created');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating storage location:', error);
      toast.error('Failed to create storage location');
    }
  };

  const resetForm = () => {
    setFormData({
      locationId: '',
      code: '',
      name: '',
      type: 'RACK',
      zone: '',
      aisle: '',
      rack: '',
      level: '',
      bin: '',
      maxWeight: '',
      maxVolume: '',
    });
  };

  const storageTypes: StorageLocationType[] = [
    'RACK',
    'BIN',
    'FLOOR',
    'OUTDOOR',
    'TANK',
    'PIPE_RACK',
    'CONTAINER',
  ];

  const storageLocationColumns: Column<YardStorageLocation>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      render: (loc) => <span className="font-mono font-medium">{loc.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
    },
    {
      key: 'location',
      header: 'Parent Location',
      render: (loc) => (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-neutral-400" />
          {loc.location.name}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (loc) => <span>{loc.type.replace('_', ' ')}</span>,
    },
    {
      key: 'zone',
      header: 'Zone',
      sortable: true,
      render: (loc) => <span>{loc.zone || '-'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (loc) => <StatusBadge status={loc.status} />,
    },
    {
      key: 'items',
      header: 'Items',
      render: (loc) => (
        <div className="flex items-center gap-1">
          <Package className="h-4 w-4 text-neutral-400" />
          {loc._count?.storedItems || 0}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (loc) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLocation(loc);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Storage Locations"
          description="Racks, bins, and zones within each yard, with the stock currently held in them."
          actions={
            <Button onClick={() => navigate('/yard/locations/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Add Location
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Locations"
            value={stats.totalLocations}
            icon={<Warehouse className="h-5 w-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Racks"
            value={stats.racks}
            icon={<Grid3x3 className="h-5 w-5" />}
            sentiment="success"
          />
          <MetricCard
            label="Bins"
            value={stats.bins}
            icon={<Layers className="h-5 w-5" />}
            sentiment="neutral"
          />
          <MetricCard
            label="Utilization"
            value={`${stats.utilization}%`}
            icon={<Activity className="h-5 w-5" />}
            sentiment="info"
          />
        </KPIGrid>
      </motion.div>

      {/* Table */}
      <motion.div variants={fadeUp}>
        <DataTable<YardStorageLocation>
          toolbar={
            <TableFilters
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by code or name..."
              filters={[
                {
                  id: 'status',
                  label: 'Statuses',
                  value: statusFilter,
                  options: [
                    { value: 'AVAILABLE', label: 'Available' },
                    { value: 'OCCUPIED', label: 'Occupied' },
                    { value: 'RESERVED', label: 'Reserved' },
                    { value: 'MAINTENANCE', label: 'Maintenance' },
                    { value: 'BLOCKED', label: 'Blocked' },
                  ],
                  onChange: setStatusFilter,
                  placeholder: 'Status',
                },
                {
                  id: 'type',
                  label: 'Types',
                  value: typeFilter,
                  options: storageTypes.map((type) => ({
                    value: type,
                    label: type.replace('_', ' '),
                  })),
                  onChange: setTypeFilter,
                  placeholder: 'Type',
                },
              ]}
            />
          }
          onRowClick={(loc) => setSelectedLocation(loc)}
          data={storageLocations}
          columns={storageLocationColumns}
          loading={loading}
          pagination
          emptyTitle="No storage locations found"
          emptyDescription="Create a new storage location to get started."
        />
      </motion.div>

      {/* Create Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle>Create Storage Location</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label>Parent Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  placeholder="e.g., A-01-03"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  placeholder="Location name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => setFormData({ ...formData, type: v as StorageLocationType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {storageTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zone</Label>
                <Input
                  placeholder="Zone"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Aisle</Label>
                <Input
                  placeholder="Aisle"
                  value={formData.aisle}
                  onChange={(e) => setFormData({ ...formData, aisle: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Rack</Label>
                <Input
                  placeholder="Rack"
                  value={formData.rack}
                  onChange={(e) => setFormData({ ...formData, rack: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Level</Label>
                <Input
                  placeholder="Level"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Bin</Label>
                <Input
                  placeholder="Bin"
                  value={formData.bin}
                  onChange={(e) => setFormData({ ...formData, bin: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Weight (kg)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.maxWeight}
                  onChange={(e) => setFormData({ ...formData, maxWeight: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Volume (m³)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.maxVolume}
                  onChange={(e) => setFormData({ ...formData, maxVolume: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button className="flex-1" onClick={handleCreate}>
                Create Location
              </Button>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* View Details Sheet */}
      <Sheet open={!!selectedLocation} onOpenChange={() => setSelectedLocation(null)}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          {selectedLocation && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedLocation.name}</SheetTitle>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Code</p>
                    <p className="font-mono font-medium">{selectedLocation.code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Type</p>
                    <p>{selectedLocation.type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <StatusBadge status={selectedLocation.status} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Zone</p>
                    <p>{selectedLocation.zone || '-'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Parent Location</p>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-neutral-400" />
                    <span>{selectedLocation.location.name}</span>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Position</p>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="bg-neutral-100 p-2 rounded">
                      <p className="text-slate-500">Aisle</p>
                      <p className="font-medium">{selectedLocation.aisle || '-'}</p>
                    </div>
                    <div className="bg-neutral-100 p-2 rounded">
                      <p className="text-slate-500">Rack</p>
                      <p className="font-medium">{selectedLocation.rack || '-'}</p>
                    </div>
                    <div className="bg-neutral-100 p-2 rounded">
                      <p className="text-slate-500">Level</p>
                      <p className="font-medium">{selectedLocation.level || '-'}</p>
                    </div>
                    <div className="bg-neutral-100 p-2 rounded">
                      <p className="text-slate-500">Bin</p>
                      <p className="font-medium">{selectedLocation.bin || '-'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Capacity</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-neutral-100 p-2 rounded">
                      <p className="text-slate-500">Max Weight</p>
                      <p className="font-medium">
                        {selectedLocation.maxWeight ? `${selectedLocation.maxWeight} kg` : '-'}
                      </p>
                    </div>
                    <div className="bg-neutral-100 p-2 rounded">
                      <p className="text-slate-500">Max Volume</p>
                      <p className="font-medium">
                        {selectedLocation.maxVolume ? `${selectedLocation.maxVolume} m³` : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Stored Items</p>
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-neutral-400" />
                    <span className="text-lg font-medium">
                      {selectedLocation._count?.storedItems || 0} items
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </motion.main>
  );
}
